<!-- components/forms/URLForm.vue -->
<script setup>
import { ref, computed, watch } from "vue"
import {
  LinkIcon,
  GlobeAltIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  ClipboardDocumentIcon,
  XMarkIcon
} from "@heroicons/vue/24/outline"

// Props & Emits
const props = defineProps({
  modelValue: {
    type: String,
    default: ""
  }
})

const emit = defineEmits(["update:modelValue"])

// Local reactive state
const localUrl = ref(props.modelValue || "")
const isValidating = ref(false)
const validationMessage = ref("")
const showProtocolHelper = ref(false)

// Computed
const isValidUrl = computed(() => {
  if (!localUrl.value) return false
  try {
    new URL(localUrl.value)
    return true
  } catch {
    return false
  }
})

const urlPreview = computed(() => {
  if (!isValidUrl.value) return ""
  try {
    const urlObj = new URL(localUrl.value)
    return `${urlObj.protocol}//${urlObj.hostname}${urlObj.pathname}`
  } catch {
    return localUrl.value
  }
})

const urlDomain = computed(() => {
  if (!isValidUrl.value) return ""
  try {
    const urlObj = new URL(localUrl.value)
    return urlObj.hostname
  } catch {
    return ""
  }
})

const hasProtocol = computed(() => {
  return localUrl.value.includes("://") || localUrl.value.startsWith("tel:") || localUrl.value.startsWith("mailto:")
})

const validationStatus = computed(() => {
  if (!localUrl.value) return { type: 'neutral', message: 'Enter a URL' }
  if (localUrl.value.length < 3) return { type: 'error', message: 'URL too short' }
  if (localUrl.value.length > 2048) return { type: 'error', message: 'URL too long (max 2048 characters)' }
  if (!hasProtocol.value) return { type: 'warning', message: 'Missing protocol (http://, https://, etc.)' }
  if (!isValidUrl.value) return { type: 'error', message: 'Invalid URL format' }
  return { type: 'success', message: 'Valid URL' }
})

const commonProtocols = [
  { label: "HTTPS", value: "https://", description: "Secure web page", icon: "🔒" },
  { label: "HTTP", value: "http://", description: "Web page", icon: "🌐" },
  { label: "FTP", value: "ftp://", description: "File transfer", icon: "📁" },
  { label: "Tel", value: "tel:", description: "Phone number", icon: "📞" },
  { label: "mailto", value: "mailto:", description: "Email address", icon: "📧" }
]

const commonDomains = [
  "https://www.google.com",
  "https://www.youtube.com", 
  "https://www.facebook.com",
  "https://www.twitter.com",
  "https://www.instagram.com",
  "https://www.linkedin.com"
]

// Methods
const updateValue = () => {
  emit('update:modelValue', localUrl.value)
}

const addProtocol = (protocol) => {
  if (localUrl.value && !hasProtocol.value) {
    localUrl.value = protocol + localUrl.value
  } else if (!localUrl.value) {
    localUrl.value = protocol
    showProtocolHelper.value = false
    // Focus input after adding protocol
    setTimeout(() => {
      const input = document.querySelector('#url-input')
      if (input) input.focus()
    }, 100)
  }
}

const clearUrl = () => {
  localUrl.value = ""
  showProtocolHelper.value = false
}

const pasteFromClipboard = async () => {
  try {
    const text = await navigator.clipboard.readText()
    if (text) {
      localUrl.value = text.trim()
    }
  } catch (error) {
    console.warn("Failed to read clipboard:", error)
  }
}

const selectCommonDomain = (domain) => {
  localUrl.value = domain
}

const autoAddHttps = () => {
  if (localUrl.value && !hasProtocol.value) {
    // Check if it looks like a domain (contains a dot and no spaces)
    if (localUrl.value.includes('.') && !localUrl.value.includes(' ')) {
      localUrl.value = 'https://' + localUrl.value
    }
  }
}

// Watchers
watch(localUrl, (newUrl) => {
  updateValue()
}, { immediate: false })

// Watch for external prop changes
watch(() => props.modelValue, (newValue) => {
  if (newValue !== localUrl.value) {
    localUrl.value = newValue || ""
  }
}, { immediate: true })

// Auto-add https when user leaves input
const handleBlur = () => {
  autoAddHttps()
}

const handleFocus = () => {
  if (!localUrl.value && !showProtocolHelper.value) {
    showProtocolHelper.value = true
  }
}
</script>

<template>
  <div class="bg-white rounded-lg border border-gray-200 p-4 tablet:p-6 min-w-0">
    <div class="flex items-center space-x-2 mb-4 min-w-0">
      <LinkIcon class="w-5 h-5 text-blue-600" />
      <h3 class="text-lg font-semibold text-gray-800">Website URL</h3>
    </div>

    <!-- URL Input -->
    <div class="space-y-4">
      <div>
        <label for="url-input" class="block text-sm font-medium text-gray-700 mb-2">
          Enter URL or Website Address
        </label>
        
        <div class="relative min-w-0">
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <GlobeAltIcon class="h-5 w-5 text-gray-400" />
          </div>
          
          <input
            id="url-input"
            v-model="localUrl"
            type="url"
            placeholder="https://example.com"
            @focus="handleFocus"
            @blur="handleBlur"
            class="block w-full min-w-0 pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            :class="{
              'border-red-300 focus:border-red-500 focus:ring-red-500': validationStatus.type === 'error',
              'border-yellow-300 focus:border-yellow-500 focus:ring-yellow-500': validationStatus.type === 'warning',
              'border-green-300 focus:border-green-500 focus:ring-green-500': validationStatus.type === 'success'
            }"
          />

          <!-- Clear button (44px touch target on mobile) -->
          <button
            v-if="localUrl"
            type="button"
            @click="clearUrl"
            class="absolute inset-y-0 right-0 pr-2 flex items-center justify-center min-w-[44px] min-h-[44px] text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon class="h-5 w-5" />
          </button>
        </div>

        <!-- Validation Message -->
        <div class="mt-2 flex items-center space-x-2">
          <component
            :is="validationStatus.type === 'error' ? ExclamationCircleIcon : 
                validationStatus.type === 'warning' ? ExclamationCircleIcon : 
                validationStatus.type === 'success' ? CheckCircleIcon : GlobeAltIcon"
            class="w-4 h-4"
            :class="{
              'text-red-500': validationStatus.type === 'error',
              'text-yellow-500': validationStatus.type === 'warning', 
              'text-green-500': validationStatus.type === 'success',
              'text-gray-400': validationStatus.type === 'neutral'
            }"
          />
          <span 
            class="text-sm"
            :class="{
              'text-red-600': validationStatus.type === 'error',
              'text-yellow-600': validationStatus.type === 'warning',
              'text-green-600': validationStatus.type === 'success', 
              'text-gray-500': validationStatus.type === 'neutral'
            }"
          >
            {{ validationStatus.message }}
          </span>
        </div>
      </div>

      <!-- Protocol Helper -->
      <div v-if="showProtocolHelper || !hasProtocol" class="space-y-3">
        <div class="flex items-center justify-between">
          <label class="text-sm font-medium text-gray-700">Quick Protocol</label>
          <button
            @click="showProtocolHelper = !showProtocolHelper"
            class="text-sm text-blue-600 hover:text-blue-800"
          >
            {{ showProtocolHelper ? 'Hide' : 'Show' }} Options
          </button>
        </div>
        
        <div v-if="showProtocolHelper" class="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <button
            v-for="protocol in commonProtocols"
            :key="protocol.value"
            type="button"
            @click="addProtocol(protocol.value)"
            class="p-3 min-h-[44px] text-left border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
          >
            <div class="flex items-center space-x-2">
              <span class="text-lg">{{ protocol.icon }}</span>
              <div>
                <div class="font-medium text-sm">{{ protocol.label }}</div>
                <div class="text-xs text-gray-500">{{ protocol.description }}</div>
              </div>
            </div>
          </button>
        </div>
      </div>

      <!-- URL Actions -->
      <div class="flex space-x-3">
        <button
          @click="pasteFromClipboard"
          class="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
        >
          <ClipboardDocumentIcon class="w-4 h-4" />
          <span>Paste</span>
        </button>
        
        <button
          v-if="!hasProtocol && localUrl"
          @click="addProtocol('https://')"
          class="flex items-center space-x-2 px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition-colors"
        >
          <span>🔒</span>
          <span>Add HTTPS</span>
        </button>
      </div>

      <!-- Common Domains -->
      <div v-if="!localUrl" class="space-y-3">
        <label class="text-sm font-medium text-gray-700">Popular Websites</label>
        <div class="grid grid-cols-2 gap-2">
          <button
            v-for="domain in commonDomains"
            :key="domain"
            @click="selectCommonDomain(domain)"
            class="p-2 text-left text-sm border border-gray-200 rounded hover:border-blue-300 hover:bg-blue-50 transition-colors"
          >
            {{ domain.replace('https://www.', '') }}
          </button>
        </div>
      </div>

      <!-- URL Preview -->
      <div v-if="isValidUrl" class="bg-gray-50 p-4 rounded-lg">
        <div class="flex items-center space-x-2 mb-2">
          <CheckCircleIcon class="w-4 h-4 text-green-500" />
          <span class="text-sm font-medium text-gray-700">URL Preview</span>
        </div>
        <div class="space-y-1">
          <div class="text-sm text-gray-600">Domain: <span class="font-medium">{{ urlDomain }}</span></div>
          <div class="text-sm text-gray-600 break-all">Full URL: <span class="font-mono">{{ urlPreview }}</span></div>
        </div>
      </div>

      <!-- Tips -->
      <div class="bg-blue-50 p-3 rounded-lg">
        <h4 class="text-sm font-medium text-blue-800 mb-1">💡 Tips</h4>
        <ul class="text-xs text-blue-700 space-y-1">
          <li>• URLs should include the protocol (https://, http://, etc.)</li>
          <li>• Use HTTPS for secure websites</li>
          <li>• Test your URL in a browser before creating the QR code</li>
          <li>• Shorter URLs create cleaner QR codes</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Input focus animation */
input:focus {
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Protocol button hover effects */
button:hover {
  transform: translateY(-1px);
}

button:active {
  transform: translateY(0);
}

/* URL validation states */
.border-red-300 {
  border-color: #fca5a5;
}

.border-yellow-300 {
  border-color: #fcd34d;
}

.border-green-300 {
  border-color: #86efac;
}

/* Smooth transitions */
.transition-colors {
  transition-property: color, background-color, border-color;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}

/* Protocol helper animation */
.protocol-helper-enter-active,
.protocol-helper-leave-active {
  transition: opacity 0.3s ease;
}

.protocol-helper-enter-from,
.protocol-helper-leave-to {
  opacity: 0;
}
</style>